// src/context/LoadingContext.jsx
// -----------------------------------------------------------------------------
// Context provider to manage global API loader visibility state.
// Subscribes to loadingStore to react to outstanding request count changes.
// -----------------------------------------------------------------------------
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import loadingStore from '../api/loadingStore';

const LoadingContext = createContext();

export function LoadingProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Processing request...');

  useEffect(() => {
    // Subscribe to loadingStore events to dynamically update loading state
    const unsubscribe = loadingStore.subscribe((count, message) => {
      setIsLoading(count > 0);
      setLoadingMessage(message || 'Processing request...');
    });
    return unsubscribe;
  }, []);

  const showLoader = useCallback((message = 'Processing request...') => {
    loadingStore.show(message);
  }, []);

  const hideLoader = useCallback(() => {
    loadingStore.hide();
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, loadingMessage, showLoader, hideLoader }}>
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}

export default LoadingContext;

